import gql from 'graphql-tag';

import { mapPersonaForBackendInput } from 'src/data/persona';
import { executeGqlMutation } from 'src/graphql/client';
import { stringifyIfPresent } from 'src/lib/common';
import { getSingleProp } from 'src/lib/graphql';

import { FRAGMENT_PROMPT_BLOCKS } from './schema';

const MUTATION = gql`
  mutation SetupMultiPromptRunner(
    $projectId: ID!
    $generationTool: GenerationToolEnum!
    $subGenerationToolsProps: JSON
    $inputText: String
    $scoringMethod: ScoringMethodInput
    $suggestionMode: String
    $language: String!
    $languageTargetFormality: LanguageTargetFormalityEnum
    $numberOfVariations: Int!
    $tov: String
    $customTovId: ID
    $debugAcPrompt: String
    $freestyleToolId: ID
    $freestyleToolAllInstructions: String
    $freestyleToolAllInstructionsData: JSON
    $customerPersonaId: ID
    $customerPersona: CustomerPersonaInput
    $annotations: [SuggestionAnnotationInput!]
    $sourceType: String
    $url: String
    $manualInstructions: String
    $avoidWords: [String!]
    $onlyImproveScore: Boolean
    $originalVariationId: ID
    $extractedBrief: String
    $urlExtractions: [UrlExtractionInput!]
    $callToAction: String
    $maxChars: Int
    $promptFields: JSON
    $promptFieldImages: JSON
    $promptFieldDocuments: JSON
    $customMultiPromptId: ID
    $csvId: ID
    $brandVoiceIdsForBrandTerms: [ID!]
    $associatedResources: [AssociatedResourceInput!]
    $overrideBlocks: JSON
    $tovInput: TovInput
    $structureInstructionsData: JSON
  ) {
    setupMultiPromptRunner(
      projectId: $projectId
      generationTool: $generationTool
      subGenerationToolsProps: $subGenerationToolsProps
      inputText: $inputText
      scoringMethod: $scoringMethod
      suggestionMode: $suggestionMode
      language: $language
      languageTargetFormality: $languageTargetFormality
      numberOfVariations: $numberOfVariations
      tov: $tov
      customTovId: $customTovId
      debugAcPrompt: $debugAcPrompt
      freestyleToolId: $freestyleToolId
      freestyleToolAllInstructions: $freestyleToolAllInstructions
      freestyleToolAllInstructionsData: $freestyleToolAllInstructionsData
      customerPersonaId: $customerPersonaId
      customerPersona: $customerPersona
      annotations: $annotations
      sourceType: $sourceType
      url: $url
      manualInstructions: $manualInstructions
      avoidWords: $avoidWords
      onlyImproveScore: $onlyImproveScore
      originalVariationId: $originalVariationId
      extractedBrief: $extractedBrief
      urlExtractions: $urlExtractions
      callToAction: $callToAction
      maxChars: $maxChars
      promptFields: $promptFields
      promptFieldImages: $promptFieldImages
      promptFieldDocuments: $promptFieldDocuments
      customMultiPromptId: $customMultiPromptId
      csvId: $csvId
      brandVoiceIdsForBrandTerms: $brandVoiceIdsForBrandTerms
      associatedResources: $associatedResources
      overrideBlocks: $overrideBlocks
      tovInput: $tovInput
      structureInstructionsData: $structureInstructionsData
    ) {
      ...PromptBlocks
    }
  }
  ${FRAGMENT_PROMPT_BLOCKS}
`;

export const setupMultiPromptRunner = async ({
  projectId,
  generationTool,
  subGenerationToolsProps,
  inputText,
  scoringMethod,
  suggestionMode,
  language,
  numberOfVariations,
  tov,
  customTovId,
  debugAcPrompt,
  freestyleToolId,
  freestyleToolAllInstructions,
  freestyleToolAllInstructionsData,
  languageTargetFormality,
  customerPersona,
  annotations,
  sourceType,
  url,
  manualInstructions,
  avoidWords,
  onlyImproveScore,
  originalVariationId,
  extractedBrief,
  urlExtractions,
  callToAction,
  maxChars,
  promptFields,
  promptFieldImages,
  promptFieldDocuments,
  customMultiPromptId,
  csvId,
  brandVoiceIdsForBrandTerms,
  associatedResources,
  overrideBlocks,
  tovInput,
  structureInstructionsData,
}) => {
  const variables = {
    projectId,
    generationTool,
    subGenerationToolsProps: stringifyIfPresent(subGenerationToolsProps),
    inputText,
    scoringMethod,
    suggestionMode,
    language,
    numberOfVariations,
    tov,
    customTovId,
    debugAcPrompt,
    freestyleToolId,
    freestyleToolAllInstructions,
    freestyleToolAllInstructionsData: stringifyIfPresent(freestyleToolAllInstructionsData),
    languageTargetFormality,
    customerPersonaId: customerPersona?.id,
    customerPersona: mapPersonaForBackendInput(customerPersona),
    annotations,
    sourceType,
    url,
    manualInstructions,
    avoidWords,
    onlyImproveScore,
    originalVariationId,
    extractedBrief,
    urlExtractions,
    callToAction,
    maxChars,
    promptFields: stringifyIfPresent(promptFields),
    promptFieldImages: stringifyIfPresent(promptFieldImages),
    promptFieldDocuments: stringifyIfPresent(promptFieldDocuments),
    customMultiPromptId,
    csvId,
    brandVoiceIdsForBrandTerms,
    associatedResources,
    overrideBlocks: stringifyIfPresent(overrideBlocks),
    tovInput,
    structureInstructionsData: stringifyIfPresent(structureInstructionsData),
  };

  const response = await executeGqlMutation({
    mutation: MUTATION,
    variables,
    options: {
      traceAcRequests: true,
    },
  });
  return getSingleProp(response.data);
};
